/*
 * Drop a Hint (snippets/bc-drop-hint.liquid).
 *
 * One <dialog> is built on first open and appended to <body>, so a customizer
 * sibling swap (which replaces .product__info-container innerHTML) never touches it.
 * Product data is re-read from the [data-bc-hint-product] JSON next to the clicked
 * trigger at OPEN time, and the variant/price from what is on screen right then.
 *
 * Sends through the bc-hint Worker (Experiments/bc-hint-worker), never to Klaviyo directly:
 * the Worker verifies Cloudflare Turnstile, filters the name/note, rate-limits, looks the
 * product up itself and sends the Klaviyo events server-side with a secret the flows require.
 * Only the handle + variant id go up; the Worker never trusts product text from the page.
 * Turnstile (invisible unless Cloudflare wants a click) loads on first open only.
 */
(function () {
  'use strict';
  if (window.__bcDropHint) return;
  window.__bcDropHint = true;

  var ENDPOINT = 'https://bc-hint.projects-8b0.workers.dev/hint';
  var TURNSTILE_SITEKEY = '0x4AAAAAAFTBZN7l0uGsOonX';
  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var TOKEN_WAIT_MS = 15000;
  var CAP = 5;
  var WINDOW_MS = 24 * 60 * 60 * 1000;
  var LOG_KEY = 'bc_hint_log';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  var dialog = null;
  var current = null; // { data, variant, price, metal, image, title, url }
  var opener = null;  // the trigger that opened the dialog; focus returns here
  var closing = false;
  var closeTimer = 0;
  var EXIT_MS = 160;  // matches the base .bc-hint transition in bc-drop-hint.css
  var openedAt = 0;   // modal-open time; the Worker refuses submits under 3s (bot signal)

  // Turnstile: one widget inside the dialog, rendered on first open. Tokens are single-use,
  // so the widget is reset after every submit attempt.
  var ts = { loading: null, widgetId: null, token: '', failed: false };

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (ts.loading) return ts.loading;
    ts.loading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.onload = function () { window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing')); };
      s.onerror = function () { ts.loading = null; reject(new Error('turnstile blocked')); };
      document.head.appendChild(s);
    });
    return ts.loading;
  }

  function renderTurnstile() {
    if (ts.widgetId !== null) return;
    loadTurnstile().then(function (turnstile) {
      if (ts.widgetId !== null || !dialog) return;
      ts.widgetId = turnstile.render(dialog.querySelector('.bc-hint__turnstile'), {
        sitekey: TURNSTILE_SITEKEY,
        action: 'drop-hint',
        appearance: 'interaction-only',
        size: 'flexible',
        callback: function (t) { ts.token = t; ts.failed = false; },
        'expired-callback': function () { ts.token = ''; },
        'error-callback': function () { ts.token = ''; ts.failed = true; }
      });
    }).catch(function () { ts.failed = true; });
  }

  function resetTurnstile() {
    ts.token = '';
    if (window.turnstile && ts.widgetId !== null) { try { window.turnstile.reset(ts.widgetId); } catch (e) { /* ignore */ } }
  }

  // Resolves with a token, waiting for an invisible check (or a click) to finish.
  function getToken() {
    return new Promise(function (resolve, reject) {
      var start = Date.now();
      (function poll() {
        if (ts.token) return resolve(ts.token);
        if (ts.failed || Date.now() - start > TOKEN_WAIT_MS) return reject(new Error('turnstile'));
        setTimeout(poll, 150);
      })();
    });
  }

  function readLog() {
    try {
      var raw = window.localStorage.getItem(LOG_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      var now = Date.now();
      return Array.isArray(arr) ? arr.filter(function (t) { return typeof t === 'number' && now - t < WINDOW_MS; }) : [];
    } catch (e) {
      return [];
    }
  }
  function writeLog(arr) {
    try { window.localStorage.setItem(LOG_KEY, JSON.stringify(arr)); } catch (e) { /* storage blocked */ }
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'text') node.textContent = attrs[k];
      else if (k === 'className') node.className = attrs[k];
      else node.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) { if (c) node.appendChild(c); });
    return node;
  }

  function field(id, label, type, autocomplete) {
    var input = el('input', { id: id, name: id, type: type, autocomplete: autocomplete, className: 'bc-hint__input', 'aria-describedby': id + '-err' });
    if (type === 'email') { input.setAttribute('inputmode', 'email'); input.setAttribute('spellcheck', 'false'); }
    return el('div', { className: 'bc-hint__field' }, [
      el('label', { for: id, className: 'bc-hint__label', text: label }),
      input,
      el('p', { id: id + '-err', className: 'bc-hint__error', 'aria-live': 'polite' })
    ]);
  }

  function build() {
    var d = el('dialog', { className: 'bc-hint', 'aria-labelledby': 'bc-hint-title', 'aria-describedby': 'bc-hint-sub' });

    var close = el('button', { type: 'button', className: 'bc-hint__close', 'aria-label': 'Close', 'data-bc-hint-close': '' });
    close.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19"/></svg>';

    var form = el('form', { className: 'bc-hint__form', novalidate: '' });

    var noteWrap = el('div', { className: 'bc-hint__note', hidden: '' }, [
      el('label', { for: 'bc-hint-note', className: 'bc-hint__label', text: 'Note' }),
      el('textarea', { id: 'bc-hint-note', name: 'note', maxlength: '300', rows: '3', className: 'bc-hint__input bc-hint__textarea', 'aria-describedby': 'bc-hint-note-count' }),
      el('p', { id: 'bc-hint-note-count', className: 'bc-hint__count', text: '0/300' })
    ]);
    var noteToggle = el('button', { type: 'button', className: 'bc-hint__note-toggle', 'aria-expanded': 'false', 'aria-controls': 'bc-hint-note-wrap' });
    noteToggle.innerHTML = '<span class="bc-hint__underline">Add note</span> (optional)';
    noteWrap.id = 'bc-hint-note-wrap';

    function check(id, label, checked) {
      var input = el('input', { type: 'checkbox', id: id, name: id, className: 'bc-hint__checkbox' });
      input.checked = !!checked;
      return el('div', { className: 'bc-hint__check' }, [input, el('label', { for: id, text: label })]);
    }

    // Honeypot: off-screen, not tabbable, ignored by autofill. Bots fill it.
    var honey = el('div', { className: 'bc-hint__hp', 'aria-hidden': 'true' }, [
      el('label', { for: 'bc-hint-website', text: 'Website' }),
      el('input', { type: 'text', id: 'bc-hint-website', name: 'website', tabindex: '-1', autocomplete: 'off' })
    ]);

    var submit = el('button', { type: 'submit', className: 'bc-hint__submit' }, [el('span', { text: 'Send' })]);

    var privacy = el('p', { className: 'bc-hint__legal' });
    privacy.innerHTML = 'By submitting this form, you agree that Beyond Carat may use your email address in accordance with our Privacy Policy. <a href="/policies/privacy-policy" target="_blank" rel="noopener">View privacy policy</a>.';

    [
      field('bc-hint-to', "Recipient's email", 'email', 'off'),
      field('bc-hint-from', 'Your email', 'email', 'email'),
      field('bc-hint-name', 'Your name', 'text', 'name'),
      noteToggle,
      noteWrap,
      honey,
      check('bc-hint-copy', 'Send me a copy of this email', true),
      check('bc-hint-optin', 'Send me Beyond Carat email updates', false),
      el('div', { className: 'bc-hint__turnstile' }),
      el('p', { className: 'bc-hint__status', role: 'status', 'aria-live': 'polite' }),
      submit,
      privacy
    ].forEach(function (n) { form.appendChild(n); });

    var product = el('div', { className: 'bc-hint__product' }, [
      el('div', { className: 'bc-hint__thumb' }, [el('img', { alt: '', width: '96', height: '96', loading: 'eager' })]),
      el('div', { className: 'bc-hint__product-text' }, [
        el('p', { className: 'bc-hint__product-title' }),
        el('p', { className: 'bc-hint__product-meta' })
      ])
    ]);

    var main = el('div', { className: 'bc-hint__main' }, [
      el('h2', { id: 'bc-hint-title', className: 'bc-hint__title', text: 'Drop a Hint' }),
      el('p', { id: 'bc-hint-sub', className: 'bc-hint__sub', text: 'Love this design? Share a little hint.' }),
      product,
      form
    ]);

    var done = el('div', { className: 'bc-hint__done', hidden: '' }, [
      el('h2', { className: 'bc-hint__title bc-hint__done-title', tabindex: '-1', text: 'Hint sent.' }),
      el('p', { className: 'bc-hint__sub bc-hint__done-text' }),
      el('button', { type: 'button', className: 'bc-hint__submit', 'data-bc-hint-close': '' }, [el('span', { text: 'Close' })])
    ]);

    d.appendChild(el('div', { className: 'bc-hint__panel' }, [close, main, done]));
    document.body.appendChild(d);

    // Wiring
    d.addEventListener('click', function (e) {
      if (e.target === d) return closeDialog(); // backdrop
      if (e.target.closest('[data-bc-hint-close]')) closeDialog();
    });
    // Esc: run the animated close instead of the instant native one.
    d.addEventListener('cancel', function (e) {
      e.preventDefault();
      closeDialog();
    });
    // Any close that skipped closeDialog (a browser force-closing after repeated Esc) still
    // unlocks the page and resets state. Idempotent with finishClose().
    // (Skipped when already reopened: the event from our own close() is queued as a task.)
    d.addEventListener('close', function () { if (!d.open) finishClose(); });
    d.addEventListener('transitionend', function (e) {
      if (closing && e.target === d && e.propertyName === 'opacity') finishClose();
    });

    noteToggle.addEventListener('click', function () {
      var open = noteWrap.hasAttribute('hidden');
      if (open) { noteWrap.removeAttribute('hidden'); noteToggle.setAttribute('aria-expanded', 'true'); d.querySelector('#bc-hint-note').focus(); }
      else { noteWrap.setAttribute('hidden', ''); noteToggle.setAttribute('aria-expanded', 'false'); }
    });
    var note = d.querySelector('#bc-hint-note');
    note.addEventListener('input', function () {
      d.querySelector('#bc-hint-note-count').textContent = note.value.length + '/300';
    });

    ['bc-hint-to', 'bc-hint-from', 'bc-hint-name'].forEach(function (id) {
      d.querySelector('#' + id).addEventListener('input', function () { setError(id, ''); });
    });
    note.addEventListener('input', function () { setStatus(''); });

    form.addEventListener('submit', onSubmit);
    return d;
  }

  function setError(id, msg) {
    var input = dialog.querySelector('#' + id);
    var err = dialog.querySelector('#' + id + '-err');
    err.textContent = msg;
    if (msg) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }

  function setStatus(msg, isError) {
    var s = dialog.querySelector('.bc-hint__status');
    s.textContent = msg || '';
    s.classList.toggle('is-error', !!isError);
  }

  function readJSON(node) {
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  function visible(node) {
    return !!(node && node.offsetParent !== null && getComputedStyle(node).display !== 'none');
  }

  function tidyPrice(s) {
    return String(s || '').replace(/\s+/g, ' ').trim().replace(/([.,])00(?=\D*$)/, '');
  }

  // What is on screen right now: variant id from the live product form, price from
  // the visible price block, product/metal from the JSON beside the trigger.
  function snapshot(trigger) {
    var wrap = trigger.closest('.bc-drop-hint');
    var dataNode = (wrap && wrap.querySelector('[data-bc-hint-product]')) ||
      document.querySelector('.product__info-container [data-bc-hint-product]');
    var data = dataNode ? readJSON(dataNode) : null;
    if (!data) return null;

    var scope = trigger.closest('.product__info-container') || document;
    var idInput = scope.querySelector('product-form input[name="id"], form[action*="/cart/add"] input[name="id"]');
    var vid = idInput && idInput.value ? Number(idInput.value) : data.selectedVariantId;
    var variant = (data.variants || []).filter(function (v) { return Number(v.id) === Number(vid); })[0] ||
      (data.variants || []).filter(function (v) { return Number(v.id) === Number(data.selectedVariantId); })[0] ||
      (data.variants || [])[0] || {};

    var price = '';
    var sale = scope.querySelector('#js-sale-wrapper');
    var saleP = scope.querySelector('#js-sale-price');
    var regP = scope.querySelector('#js-regular-price');
    if (sale && visible(sale) && saleP) price = saleP.textContent;
    else if (regP && regP.textContent.trim()) price = regP.textContent;
    price = tidyPrice(price) || tidyPrice(variant.price);

    // Metal: "14k White Gold" when the variant option is a purity, else the metal metafield.
    var metal = data.metal || '';
    var names = (data.optionNames || []).map(function (n) { return String(n).toLowerCase(); });
    (variant.options || []).forEach(function (val, i) {
      var n = names[i] || '';
      if (n === 'purity' || n === 'metal purity' || n === 'karat') {
        if (metal.toLowerCase().indexOf(String(val).toLowerCase()) === -1) metal = (val + ' ' + metal).trim();
      } else if (n === 'metal' && !metal) {
        metal = String(val);
      }
    });

    var origin = String(data.origin || 'https://beyondcarat.com').replace(/^http:/, 'https:').replace(/\/$/, '');
    return {
      data: data,
      variantId: variant.id || vid,
      title: data.title,
      metal: metal,
      price: price,
      image: variant.image || data.image || '',
      url: origin + '/products/' + data.handle + (variant.id ? '?variant=' + variant.id : '')
    };
  }

  function resetForm() {
    var form = dialog.querySelector('form');
    form.reset();
    dialog.querySelector('#bc-hint-copy').checked = true;
    dialog.querySelector('#bc-hint-optin').checked = false;
    dialog.querySelector('.bc-hint__note').setAttribute('hidden', '');
    dialog.querySelector('.bc-hint__note-toggle').setAttribute('aria-expanded', 'false');
    dialog.querySelector('#bc-hint-note-count').textContent = '0/300';
    ['bc-hint-to', 'bc-hint-from', 'bc-hint-name'].forEach(function (id) { setError(id, ''); });
    setStatus('');
    setBusy(false);
    dialog.querySelector('.bc-hint__main').removeAttribute('hidden');
    dialog.querySelector('.bc-hint__done').setAttribute('hidden', '');
  }

  function openDialog(trigger) {
    var snap = snapshot(trigger);
    if (!snap) return;
    current = snap;
    if (!dialog) dialog = build();
    resetForm();

    var img = dialog.querySelector('.bc-hint__thumb img');
    if (snap.image) { img.src = snap.image; img.alt = snap.title; img.parentNode.removeAttribute('hidden'); }
    else img.parentNode.setAttribute('hidden', '');
    dialog.querySelector('.bc-hint__product-title').textContent = snap.title;
    dialog.querySelector('.bc-hint__product-meta').textContent = [snap.metal, snap.price].filter(Boolean).join(' · ');

    var c = snap.data.customer || {};
    if (c.email) dialog.querySelector('#bc-hint-from').value = c.email;
    if (c.name) dialog.querySelector('#bc-hint-name').value = c.name;

    opener = trigger;
    openedAt = Date.now();
    renderTurnstile();
    lockScroll();
    // Reopened during the exit animation: cancel the pending close, the transition reverses.
    closing = false;
    clearTimeout(closeTimer);
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      dialog.getBoundingClientRect(); // commit the hidden start state so the enter transitions
    }
    dialog.classList.add('is-open');
    var first = dialog.querySelector('#bc-hint-to');
    if (first) first.focus({ preventScroll: true });
  }

  function lockScroll() {
    var html = document.documentElement;
    if (html.classList.contains('bc-hint-open')) return;
    // Reserve the scrollbar's space only when the page scrolls (so has a scrollbar). A short
    // page gets no gutter it never had. With overlay scrollbars (phones, Mac trackpad) the
    // gutter is 0px, so the class is harmless there.
    if (html.scrollHeight > html.clientHeight) html.classList.add('bc-hint-open--gutter');
    html.classList.add('bc-hint-open');
  }

  function closeDialog() {
    if (!dialog || !dialog.open || closing) return;
    closing = true;
    dialog.classList.remove('is-open');
    // transitionend normally finishes the close; this covers reduced-motion/no-transition
    // cases and a missed event.
    closeTimer = setTimeout(finishClose, EXIT_MS + 80);
  }

  function finishClose() {
    if (!dialog) return;
    clearTimeout(closeTimer);
    closing = false;
    dialog.classList.remove('is-open');
    if (dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
    var html = document.documentElement;
    if (!html.classList.contains('bc-hint-open')) return; // already finished
    html.classList.remove('bc-hint-open', 'bc-hint-open--gutter');
    var t = (opener && opener.isConnected && opener) ||
      document.querySelector('.product__info-container [data-bc-hint-open]') || document.querySelector('[data-bc-hint-open]');
    if (t) t.focus({ preventScroll: true });
  }

  function setBusy(on) {
    var btn = dialog.querySelector('form .bc-hint__submit');
    btn.disabled = !!on;
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    btn.firstChild.textContent = on ? 'Sending…' : 'Send';
  }

  // Server field name -> input id, for showing the Worker's message on the right field.
  var FIELD_IDS = { recipientEmail: 'bc-hint-to', senderEmail: 'bc-hint-from', senderName: 'bc-hint-name' };

  function onSubmit(e) {
    e.preventDefault();
    var q = function (s) { return dialog.querySelector(s); };
    var to = q('#bc-hint-to').value.trim();
    var from = q('#bc-hint-from').value.trim();
    var name = q('#bc-hint-name').value.trim();
    var note = q('#bc-hint-note').value.trim().slice(0, 300);
    var copy = q('#bc-hint-copy').checked;
    var optin = q('#bc-hint-optin').checked;

    var firstBad = null;
    function bad(id, msg) { setError(id, msg); if (!firstBad) firstBad = id; }
    if (!to) bad('bc-hint-to', "Enter the recipient's email.");
    else if (!EMAIL_RE.test(to)) bad('bc-hint-to', 'Enter a valid email address.');
    if (!from) bad('bc-hint-from', 'Enter your email.');
    else if (!EMAIL_RE.test(from)) bad('bc-hint-from', 'Enter a valid email address.');
    if (!name) bad('bc-hint-name', 'Enter your name.');
    if (firstBad) { setStatus(''); q('#' + firstBad).focus(); return; }

    if (q('#bc-hint-website').value) { showDone(to); return; } // honeypot: pretend success

    var log = readLog();
    if (log.length >= CAP) {
      setStatus("You've sent a few hints today. Please try again tomorrow.", true);
      return;
    }

    var snap = current;
    var data = snap.data;
    setStatus('');
    setBusy(true);
    renderTurnstile(); // no-op when already rendered; retries a script that failed to load

    getToken()
      .catch(function () {
        var err = new Error('turnstile');
        err.userMessage = "We couldn't confirm you're not a robot. Please try again.";
        throw err;
      })
      .then(function (token) {
        return fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipientEmail: to,
            senderEmail: from,
            senderName: name,
            note: note,
            sendCopy: copy,
            optIn: optin,
            productHandle: data.handle,
            variantId: Number(snap.variantId),
            turnstileToken: token,
            elapsedMs: Date.now() - openedAt
          })
        });
      })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          if (!res.ok || !body || body.ok !== true) {
            var err = new Error('status ' + res.status);
            err.userMessage = body && body.error;
            err.field = body && body.field;
            throw err;
          }
        });
      })
      .then(function () {
        resetTurnstile();
        log.push(Date.now());
        writeLog(log);
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'drop_hint_submit', product_id: String(data.productId), variant_id: String(snap.variantId) });
        } catch (err) { /* never block the UI on analytics */ }
        showDone(to);
      })
      .catch(function (err) {
        resetTurnstile();
        setBusy(false);
        var msg = (err && err.userMessage) || "Sorry, your hint didn't send. Please try again in a moment.";
        var id = err && err.field && FIELD_IDS[err.field];
        if (id) { setError(id, msg); setStatus(''); q('#' + id).focus(); }
        else setStatus(msg, true);
      });
  }

  function showDone(to) {
    dialog.querySelector('.bc-hint__main').setAttribute('hidden', '');
    var done = dialog.querySelector('.bc-hint__done');
    done.querySelector('.bc-hint__done-text').textContent = "We've let " + to + ' know.';
    done.removeAttribute('hidden');
    done.querySelector('.bc-hint__done-title').focus();
  }

  /* Express row: when Shopify's wallet script leaves the dynamic checkout block empty
     (buyer/product not eligible, script blocked), let the hint take the full row. The row
     stays 50/50 until then so the async button never causes a jump; a wallet that turns
     up late (slow connection) switches it back. A customizer swap brings a new row. */
  var SETTLE_MS = 3500;
  var settled = false;

  function checkRow(row) {
    if (!row.querySelector('.shopify-payment-button')) { row.classList.add('bc-express-row--solo'); return; }
    var wallets = row.querySelectorAll('shopify-accelerated-checkout > :not(more-payment-options-link)');
    var shown = false;
    for (var i = 0; i < wallets.length; i++) {
      // a wallet element can exist before its (closed) shadow content paints: watch its size
      if (sizeObs && !wallets[i].__bcXrSized) { wallets[i].__bcXrSized = true; sizeObs.observe(wallets[i]); }
      if (wallets[i].getBoundingClientRect().height > 0) shown = true;
    }
    if (shown) row.classList.remove('bc-express-row--solo');
    else if (settled) row.classList.add('bc-express-row--solo');
  }

  var sizeObs = window.ResizeObserver ? new ResizeObserver(function (entries) {
    entries.forEach(function (en) {
      var row = en.target.closest && en.target.closest('[data-bc-express-row]');
      if (row) checkRow(row);
    });
  }) : null;

  function watchRows() {
    var rows = document.querySelectorAll('[data-bc-express-row]');
    Array.prototype.forEach.call(rows, function (row) {
      checkRow(row);
      if (row.__bcXrObs || !window.MutationObserver) return;
      row.__bcXrObs = new MutationObserver(function () { checkRow(row); });
      row.__bcXrObs.observe(row, { childList: true, subtree: true });
    });
  }

  function settleLater() {
    setTimeout(function () { settled = true; watchRows(); }, SETTLE_MS);
  }

  if (document.readyState === 'complete') settleLater();
  else window.addEventListener('load', settleLater);
  watchRows();
  document.addEventListener('diamond:info-replaced', function () {
    settled = false;
    watchRows();
    settleLater();
  });

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-bc-hint-open]');
    if (!t) return;
    e.preventDefault();
    openDialog(t);
  });
})();
